import React, { useEffect, useState, useMemo, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Footer from '../../../components/Footer';
import GridGallery from "../../../components/GridImage";
import Includes from '../../../components/Includes';
import TourPlan from '../../../components/TourPlan';
import Header from '../../../components/Header';
import Gallery from '../../../components/Gallery';
import Title from '../../../components/Title';
import Loader from '../../../components/Loader/Loader';
import useExperienceData from '../../../hooks/useExperienceData';
import { useTravelers } from '../../../context/travelersContext';
import Availability from './components/Availability';
import BookableItems from './components/BookableItems';
import AdditionalInfo from './components/AdditionalInfo';
import Logistics from './components/Logistics';
import { getAvailableBookableItems, getBookableItemPricingRecord, useSelectedItemTotalAmount } from './components/availability-and-price-helpers';
import MetaTags from '../../../components/MetaTags';
import { postOrder } from '../../../services/experienceOrderService';
import { useCurrency } from '../../../context/currencyContext';

const Detail = () => {
    const { id: experienceId } = useParams();
    const navigate = useNavigate();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const { experience, promoter, error, loading } = useExperienceData();
    const { selectedDate, setSelectedDate, selectedItemCode, setSelectedItemCode } = useTravelers();

    const footerRef = useRef<HTMLDivElement>(null);
    const detailRef = useRef<HTMLDivElement>(null);
    const formContainer = useRef<HTMLDivElement>(null);
    const asideRef = useRef<HTMLDivElement>(null);

    const hasGalleryImages = experience?.gallery && experience.gallery.length > 0;
    const availableImages = experience ? experience.slider : [];
    const hasIncludes = !!(experience?.included?.length || experience?.notIncluded?.length);
    const hasTourPlan = experience?.plan && experience.plan.length > 0;
    const hasLogistics = Boolean(experience?.product.logistics);
    const imgIdx = Math.floor(Math.random() * availableImages.length);
    const currentUrl = window.location.protocol + "//" + window.location.host + "/" + window.location.pathname + window.location.search;



    const handleScroll = () => {
        const pageTop = window.scrollY;
        const footerRect = isMobile && footerRef.current?.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (isMobile && footerRect && asideRef.current && formContainer.current) {
            asideRef.current.style.height = `${formContainer.current.getBoundingClientRect().height}px`;
            formContainer.current.style.width = `${asideRef.current.getBoundingClientRect().width}px`;

            if (pageTop > 390) {
                setIsSticky(footerRect.top >= windowHeight);
            } else {
                setIsSticky(false);
            }
        }
    };

    const [isOrderLoading, setIsOrderLoading] = useState(false);
    const pricing = useSelectedItemTotalAmount();
    const { convert } = useCurrency();
    const { travelers } = useTravelers();

    const handleSubmit = () => {
        handleBuyClick();
    }

    const handleBuyClick = async () => {
        const selectedProductOption = experience?.product.productOptions.find(option => option.productOptionCode === selectedItemCode);
        if (!pricing || !selectedItemCode || !selectedProductOption) return;

        const { totalAmount, productCurrency, countForComission } = pricing;
        const { value: convertedCurrencyValue } = convert(totalAmount, productCurrency);

        setIsOrderLoading(true);
        try {
            await postOrder(experience?.id || '', selectedDate, `${selectedProductOption.title} - Item Code: ${selectedItemCode}`, convertedCurrencyValue, countForComission);
            const totalTravelers = Object.values(travelers).reduce((sum, count) => sum + count, 0);
            let passengersQuery = '';
            if (totalTravelers > 0) {
                passengersQuery = `?num_passengers=${totalTravelers}`;
            }
            navigate(`/experiences/${experience?.aliasUrl}/checkout/passengers${passengersQuery}`);
        } catch (e) {
            setIsOrderLoading(false);
        }
    };

    const scrollToElement = (id: string) => {
        const element = document.getElementById(id);

        if (element) {
            const rect = element.getBoundingClientRect();
            const isVisible =
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth);

            if (!isVisible) {
                element.scrollIntoView({
                    behavior: 'smooth', // Optional: 'auto' for instant scroll
                    block: 'center',    // Scroll the element into the center of the view
                });
            }
        }
    };

    

    const handlePhoneClick = () => {
        const message = encodeURI(
            "Hola! Queria consultar por el viaje *" + experience?.title + "*"
        );
        window.open("https://wa.me/5491127516586?text=" + message, "_blank")?.focus();
    };

    useEffect(() => {
        setIsSmallScreen(isMobile);

        const handleResize = () => {
            setIsSmallScreen(isMobile);
        };
        window.addEventListener('resize', handleResize, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [experienceId, navigate]);

    const availableBookableItems = (experience && selectedDate) ? getAvailableBookableItems(selectedDate, experience['availabilitySchedule']) : []
    const memoizedAvailableBookableItems = useMemo(() => {
        return (experience && selectedDate) ? getAvailableBookableItems(selectedDate, experience['availabilitySchedule']) : [];
    }, [experience, selectedDate]);

    useEffect(() => {
        if (memoizedAvailableBookableItems && !selectedItemCode) {
            const availableBookableItem = memoizedAvailableBookableItems.find(bookableItem => {
                const isAvailable = getBookableItemPricingRecord(bookableItem, selectedDate, travelers);
                return isAvailable;
            });

            if(availableBookableItem) {
                setSelectedItemCode(availableBookableItem.productOptionCode)
            }
        }
    }, [memoizedAvailableBookableItems, travelers, selectedItemCode, setSelectedItemCode, selectedDate])

    useEffect(() => {
        if (memoizedAvailableBookableItems && selectedItemCode && experience?.availabilitySchedule.bookableItems) {
            const bookableItem = experience.availabilitySchedule.bookableItems.find(option => option.productOptionCode === selectedItemCode);
            if(bookableItem) {
                const isAvailable = getBookableItemPricingRecord(bookableItem, selectedDate, travelers)
                if(!isAvailable) {
                    setSelectedItemCode(null)
                }
            } 
        }
    }, [memoizedAvailableBookableItems, travelers, selectedItemCode, setSelectedItemCode, selectedDate, experience?.availabilitySchedule.bookableItems])

    if (loading || isOrderLoading || !experience || !promoter) {
        return <Loader />;
    }


    // Type assertions for properties that exist but are not in the type
    const experienceFont = (experience as any).font;
    const experienceColors = (experience as any).colors || {};
    const primaryColor = experienceColors.primary_color;
    const secondaryColor = experienceColors.secondary_color;

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter.fullname}
                image={availableImages[imgIdx]}
                description={experience?.description}
                icon={promoter.logoURL}
                title={experience.title}
                url={currentUrl}
                font={experienceFont || null}
                primaryColor={primaryColor || null}
                secondaryColor={secondaryColor || null}
            />
            <Helmet>
                <title>{experience.title}</title>
                <link rel="icon" type="image/png" href={`${promoter.logoURL}`} sizes="16x16" />
                <meta property="og:title" content={experience.title} />
                <meta property="og:image" content={availableImages[imgIdx]}></meta>
                <meta data-react-helmet="true" name="description" content={experience?.description} />
                <meta data-react-helmet="true" property="og:description" content={experience?.description} />
                <meta data-react-helmet="true" property="og:url" content={currentUrl}></meta>
            </Helmet>
            <div className='detail' ref={detailRef}>
                <Header
                    error={error}
                    promoter={promoter}
                    loading={loading}
                    onBuyClick={() => {
                        scrollToElement('priceBox')
                    }}
                    flag={"checkout-api"}
                    handlePhoneClick={handlePhoneClick}
                    showGalleryLink={hasGalleryImages}
                    showIncludes={hasIncludes}
                    showTourPlan={hasTourPlan}
                    stock={1000}
                />

                <div className='content-wrapper'>
                    <Title
                        promoter={promoter}
                        title={experience.title}
                    />
                    <GridGallery loading={loading} error={error} data={{ slider: experience.slider }} />
                </div>

                <div className='main-content'>
                    <div className='text-section' id="datatravel">
                        {selectedDate && availableBookableItems.length > 0 && (
                            <BookableItems
                                items={availableBookableItems}
                                selectedDate={selectedDate}
                                productOptions={experience.product.productOptions}
                                cancellationPolicy={experience.product['cancellationPolicy']}
                                handleSubmit={handleSubmit}
                            />
                        )}
                        {experience?.product_description && <h2 id="datatravel-title" dangerouslySetInnerHTML={{ __html: experience.product_description }}></h2>}
                        {experience?.description && <h2 id="datatravel-title" dangerouslySetInnerHTML={{ __html: experience.description }}></h2>}
                        {experience?.secondary_description && <p className='datatravel__p' dangerouslySetInnerHTML={{ __html: experience.secondary_description }}></p>}
                        {experience?.product?.additionalInfo && (
                            <AdditionalInfo additionalInfo={experience.product.additionalInfo} />
                        )}
                        {hasIncludes && (
                            <div className='includes' id="includes">
                                <Includes data={experience} loading={false} error={undefined} />
                            </div>
                        )}
                        {hasLogistics && (
                                <Logistics experience={experience} />
                        )}
                        {hasTourPlan && (
                            <div className='tourplan' id="tourplan">
                                <TourPlan
                                    data={experience}
                                    isExperience={true}
                                />
                            </div>
                        )}
                        {hasGalleryImages && (
                            <div className='gallery' id="gallery">
                                <Gallery data={{ gallery: experience['gallery'] }} />
                            </div>
                        )}
                    </div>
                    <div className='aside' ref={asideRef}>
                        <div className={`form-container ${isSticky ? 'sticky' : ''}`} ref={formContainer} id="priceBox">
                            <Availability
                                selectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                product={experience.product}
                                availabilitySchedule={experience.availabilitySchedule}
                                price={{
                                    fromPrice: experience.availabilitySchedule.summary.fromPrice,
                                    fromPriceBeforeDiscount: experience.availabilitySchedule.summary.fromPriceBeforeDiscount,
                                    currency: experience.availabilitySchedule.currency,
                                    promoterComission: experience.price.promoter_commission
                                }}
                                handleSubmit={() => scrollToElement('bookable-items')}
                                handlePhoneClick={handlePhoneClick}
                            />
                        </div>
                    </div>
                </div>

                <Footer
                    promoter={promoter}
                    loading={loading}
                    ref={footerRef}
                />
            </div>
        </HelmetProvider>
    );
};

export default Detail;